import React from "react";
import {ListView, ListViewProps, ListViewState} from "../list-view/ListView";
import {IApplicationState} from "../../../store";
import {connect, DispatchProp} from "react-redux";
import {memoize} from "../../utils/memoize";
import {LoadingIndicator} from "../loading/LoadingIndicator";
import styles from "./comments.module.css";
import {Comment} from "./Comment";
import {IComment, ICommentsApiOptions} from "../../services/types";
import CommentsApi from "../../services/CommentsApi";
import {TweenLite, Power1, Animation} from "gsap";
import {AnimationSettingsProp} from "../../../store/settings/types";
import FormDialog from "../dialog-manager/templates/forms/FormDialog";
import {iif} from "../iif";
import Portal from "../portal/Portal";
import {trackAction} from "../../../store/common/actions";

const SCROLL_OFFSET = 600;
const PAGE_SIZE = 20;

interface CommentsProps extends ICommentsApiOptions, DispatchProp, ListViewProps, AnimationSettingsProp {
    url: string;
    questionId: number;
    scrollView: React.RefObject<HTMLDivElement>;
    dialogContainer: React.RefObject<HTMLDivElement>;
    locale: string;
    language: string;
}

interface CommentsState extends ListViewState<IComment> {
    currentCommentId?: number;
    replyTo?: IComment;
    writeMode: boolean;
}

export interface ICommentsWidget {
    writeComment: () => void;
}

class Comments extends ListView<IComment, CommentsProps, CommentsState> implements ICommentsWidget {
    private api: CommentsApi;
    private activeTweens: Animation[] = [];

    constructor(props: CommentsProps, context: any) {
        super(props, context);

        this.api = new CommentsApi(this.props);

        this.loadPageInternal = memoize(this.loadPageInternal.bind(this));
        this.rate = this.rate.bind(this);
    }

    getInitialState() {
        return {
            ...super.getInitialState(),
            writeMode: false
        };
    }

    componentDidUpdate(prevProps: Readonly<CommentsProps>, prevState: Readonly<CommentsState>, snapshot?: any): void {
        super.componentDidUpdate(prevProps, prevState, snapshot);

        if (this.state.currentCommentId && this.state.currentCommentId !== prevState.currentCommentId) {
            if (!this.scrollView.current || !this.props.animation.enabled)
                return;

            const comment = this.scrollView.current.querySelector(`[data-comment='${this.state.currentCommentId}']`) as HTMLElement;
            if (!comment)
                return;

            let tween = TweenLite.to(this.scrollView.current, 0.5, {
                scrollTop: comment.offsetTop,
                ease: Power1.easeInOut
            }).timeScale(this.props.animation.timeScale);

            this.activeTweens.push(tween);

            tween = TweenLite.from(comment, 0.5, {
                opacity: 0,
                delay: 0.35
            }).timeScale(this.props.animation.timeScale);

            this.activeTweens.push(tween);
        }
    }

    componentWillUnmount(): void {
        for (let animation of this.activeTweens) {
            animation.kill();
        }
    }

    async loadPageInternal(page: number): Promise<{ entries: IComment[]; state?: Partial<ListViewState<IComment>> }> {
        const comments = await this.api.get(this.props.url, page + 1, this.props.pageSize, false, true);

        return {
            entries: comments
        };
    }

    renderInternal() {
        const entries = this.renderEntries();

        return (
            <>
                {entries}
                {iif(this.state.writeMode, this.renderFromDialog())}
                <LoadingIndicator disabled={!this.state.loading}/>
            </>
        );
    }

    renderEntry(comment: IComment): JSX.Element | undefined {
        const isOwn = comment.user.id === this.props.userId;

        return <Comment key={comment.id}
                        isOwn={comment.user && isOwn}
                        {...comment}
                        locale={this.props.locale}
                        onReply={() => this.writeComment(comment)}
                        onRate={this.rate}/>;
    }

    renderEntries() {
        if (!this.state.entries || !this.state.entries.length)
            return;

        return (
            <div className={styles.container}>
                {this.state.entries.map(entry => this.renderEntry(entry))}
            </div>
        )
    }

    async rate(commentId: number, rate: "up" | "down") {
        if (!this.state.entries)
            return;

        try {
            const response = await this.api.rate(this.props.url, commentId, rate);
            const index = this.state.entries.findIndex(comment => comment.id === response.id);
            if (index === -1)
                return;

            this.setState({
                entries: [
                    ...this.state.entries.slice(0, index),
                    {
                        ...this.state.entries[index],
                        rating: response.rating,
                        rate: rate === 'up' ? 1 : -1
                    },
                    ...this.state.entries.slice(index + 1)
                ]
            });
        }
        catch (error) {
            this.trackError("Failed to rate comment", error);
        }
    }

    async writeComment(comment?: IComment) {
        this.setState({
            writeMode: true,
            replyTo: comment
        });

        this.props.dispatch(trackAction({
            category: "comment",
            object: this.state.replyTo ? "reply" : undefined,
            name: "start"
        }));

        if (this.scrollView.current) {
            const commentContainer = (comment
                ? this.scrollView.current.querySelector(`[data-comment='${comment.id}']`)
                : this.scrollView.current.getElementsByClassName(styles.comment)[0]) as HTMLElement;

            if (commentContainer) {
                if (this.props.animation.enabled) {
                    const tween = TweenLite.to(this.scrollView.current, 0.5, {
                        scrollTop: commentContainer.offsetTop,
                        ease: Power1.easeInOut
                    }).timeScale(this.props.animation.timeScale);

                    this.activeTweens.push(tween);
                } else {
                    this.scrollView.current.scrollTop = commentContainer.offsetTop;
                }
            }
        }
    }

    protected getScrollViewRef() {
        return this.props.scrollView;
    }

    private renderFromDialog() {
        if (!this.props.dialogContainer.current)
            return;

        const {animation} = this.props;
        const fields = [{
            type: "textarea" as any,
            name: "message",
            value: this.state.replyTo && this.state.replyTo.user
                ? this.state.replyTo.user.name + ", "
                : undefined,
            attributes: {
                required: true,
                maxLength: 240,
            }
        }];

        return (
            <Portal container={this.props.dialogContainer.current}>
                <FormDialog title={"comments.add"} fields={fields} animation={animation} close={(result) => this.post(result && result.value && result.value.message)}/>;
            </Portal>
        );
    }

    private async post(message: string) {
        const {replyTo} = this.state;
        const prefix =  this.state.replyTo && this.state.replyTo.user
            ? this.state.replyTo.user.name + ", "
            : undefined;

        this.setState({
            writeMode: false,
            replyTo: undefined
        });

        if (!message || (prefix && message.trim() === prefix.trim())) {
            this.props.dispatch(trackAction({
                category: "comment",
                object: replyTo ? "reply" : undefined,
                name: "cancel"
            }));

            return;
        }

        try {
            const comment = await this.api.post(this.props.url, message, this.props.questionId.toString(), replyTo && replyTo.id);
            if (!comment) {
                this.trackError("Failed to post comment");
                return;
            }

            this.setState({
                entries: [
                    comment,
                    ...this.state.entries
                ],
                currentCommentId: comment.id
            });

            this.props.dispatch(trackAction({
                category: "comment",
                object: replyTo ? "reply" : undefined,
                name: "submit"
            }));
        }
        catch (error) {
            this.trackError("Failed to post comment", error);
        }
    }
}

export default connect(({ player, settings }: IApplicationState) => ({
    api: settings.commentsApi,
    pageSize: PAGE_SIZE,
    scrollOffset: SCROLL_OFFSET,
    userId: player.id,
    userName: player.name,
    userImage: player.picture,
    animation: settings.animation,
    locale: settings.general.locale,
    language: settings.general.uiLanguage,
}), null, null, { forwardRef: true } as any)(Comments);